
import { Product, ProductVariation } from "@/types/product";
import { Category } from "@/types/category";
import { plentyOneApi } from "@/services/plentyOneApi";
import { roundPrice } from "@/utils/priceRounding";
import { logger } from "@/services/logger";

// CSV data structure interface
interface CsvProductData {
  itemId: string;
  variationId: string;
  variationNumber: string;
  isActive: boolean;
  isMain: boolean;
  mainVariationId: string;
  productNameDE: string;
  productNameEN: string;
  uvpPrice: number;
  b2bPrice: number;
  b2bT1Price?: number;
  b2bT2Price?: number;
  purchasePrice: number;
  attributeValues: string;
  allCategories?: string;
  allCategoryIds?: string;
}

class CsvProductService {
  private csvUrl: string | null = null;
  private urlCsvData: CsvProductData[] | null = null;
  private urlCsvTimestamp: Date | null = null;
  private subscribers: Set<() => void> = new Set();

  // Subscribe to data updates
  subscribe(callback: () => void) {
    this.subscribers.add(callback);
  }

  // Unsubscribe from data updates
  unsubscribe(callback: () => void) {
    this.subscribers.delete(callback);
  }

  // Notify all subscribers of a data change
  private notifySubscribers() {
    logger.debug('Notifying subscribers of CSV data update');
    this.subscribers.forEach(callback => callback());
  }

  // Helper function to calculate all prices from gross UVP
  private calculatePrices(grossUvp: number): {
    uvpNet: number;
    b2bPrice58: number;
    b2bPrice55: number;
  } {
    // Convert gross to net (remove 19% German VAT)
    const uvpNet = roundPrice(grossUvp / 1.19);
    
    // Calculate B2B prices
    const b2bPrice58 = roundPrice(uvpNet * 0.58);
    const b2bPrice55 = roundPrice(uvpNet * 0.55);
    
    return {
      uvpNet,
      b2bPrice58,
      b2bPrice55
    };
  }

  // Set CSV URL for URL-based fetching
  setCsvUrl(url: string) {
    this.csvUrl = url;
    logger.debug(`CSV URL set: ${url}`);
  }

  // Get CSV URL
  getCsvUrl(): string | null {
    return this.csvUrl;
  }

  // Clear URL-based data and fallback to embedded data
  clearUrlData() {
    this.urlCsvData = null;
    this.urlCsvTimestamp = null;
    logger.debug('URL-based CSV data cleared, falling back to embedded data');
    this.notifySubscribers();
  }

  // Set CSV data directly (from Edge Function response)
  async setCsvData(data: Record<string, unknown>[]) {
    this.urlCsvData = data;
    this.urlCsvTimestamp = new Date();
    logger.debug(`CSV data set directly: ${data.length} records`);
    this.notifySubscribers();
  }

  private getCsvData(): CsvProductData[] {
    // Prefer URL-based data if available, fallback to embedded data
    if (this.urlCsvData && this.urlCsvData.length > 0) {
      logger.debug(`Using URL-based CSV data: ${this.urlCsvData.length} records`);
      // Transform URL data to expected format
      return this.transformUrlCsvData(this.urlCsvData);
    }
    
    // No fallback to embedded data - return empty array
    logger.debug('No URL CSV data available - returning empty array');
    return [];
  }

  // Transform URL-based CSV data to expected format
  private transformUrlCsvData(rawData: Record<string, unknown>[]): CsvProductData[] {
    logger.debug(`Transforming URL CSV data from dot notation to flat structure: ${rawData.length} raw records`);
    
    // Debug: Check a few raw records first
    if (rawData.length > 0) {
      logger.debug('Sample rawData keys:', Object.keys(rawData[0]));
      logger.debug('First 3 raw records:', rawData.slice(0, 3).map(item => ({
        'Item.id': item['Item.id'],
        'Variation.isActive': item['Variation.isActive'],
        'Variation.isMain': item['Variation.isMain'],
        'VariationAttributeValues.attributeValues': item['VariationAttributeValues.attributeValues']
      })));
    }
    
    const transformed = rawData.map((item, index) => {
      // Enhanced boolean conversion with detailed logging for first few records
      const rawIsActive = item['Variation.isActive'] || item.isActive;
      const rawIsMain = item['Variation.isMain'] || item.isMain;
      const convertedIsActive = Boolean(rawIsActive);
      const convertedIsMain = Boolean(rawIsMain);
      
      if (index < 5) {
        logger.debug(`Record ${index}: rawIsActive=${rawIsActive} (${typeof rawIsActive}) -> ${convertedIsActive}, rawIsMain=${rawIsMain} (${typeof rawIsMain}) -> ${convertedIsMain}`);
        // Log price handling for debugging
        logger.debug(`Record ${index} prices: B2B(T1)=${item['VariationSalesPrice.price.B2B']}, B2B.T2=${item['VariationSalesPrice.price.B2B.T2']}`);
      }
      
      return {
        itemId: String(item['Item.id'] || item.itemId || ''),
        variationId: String(item['Variation.id'] || item.variationId || ''),
        variationNumber: String(item['Variation.number'] || item.variationNumber || ''),
        isActive: convertedIsActive,
        isMain: convertedIsMain,
        mainVariationId: String(item['Variation.mainVariationId'] || item.mainVariationId || ''),
        productNameDE: String(item['ItemDescription.name.DE'] || item.productNameDE || ''),
        productNameEN: String(item['ItemDescription.name.EN'] || item.productNameEN || ''),
        uvpPrice: Number(item['VariationSalesPrice.price.UVP'] || item.uvpPrice || 0),
        // Price structure: T1 uses traditional B2B field, T2 uses new B2B.T2 field
        b2bPrice: Number(item['VariationSalesPrice.price.B2B'] || item.b2bPrice || 0),
        // T1 price is the same as traditional B2B price
        b2bT1Price: Number(item['VariationSalesPrice.price.B2B'] || item.b2bPrice || 0),
        // T2 price uses new B2B.T2 field, fallback to T1 (B2B) price if not available
        b2bT2Price: Number(item['VariationSalesPrice.price.B2B.T2'] || item.b2bT2Price || item['VariationSalesPrice.price.B2B'] || item.b2bPrice || 0),
        purchasePrice: Number(item['Variation.purchasePrice'] || item.purchasePrice || 0),
        attributeValues: String(item['VariationAttributeValues.attributeValues'] || item.attributeValues || ''),
        allCategories: String(item['VariationCategory.allCategories'] || item.allCategories || ''),
        allCategoryIds: String(item['VariationCategory.allCategoryIds'] || item.allCategoryIds || '')
      };
    });
    
    // Enhanced debug logging
    if (transformed.length > 0) {
      logger.debug('Sample transformed record:', transformed[0]);
      logger.debug('Transformed boolean summary:', {
        totalRecords: transformed.length,
        isActiveTrue: transformed.filter(r => r.isActive === true).length,
        isActiveFalse: transformed.filter(r => r.isActive === false).length,
        isMainTrue: transformed.filter(r => r.isMain === true).length,
        isMainFalse: transformed.filter(r => r.isMain === false).length
      });
      
      // Price migration debug
      logger.debug('Price structure summary:', {
        recordsWithB2B_T1: transformed.filter(r => r.b2bPrice > 0).length,
        recordsWithB2BT2: transformed.filter(r => r.b2bT2Price > 0 && r.b2bT2Price !== r.b2bPrice).length,
        samplePrices: transformed.slice(0, 3).map(r => ({
          variationId: r.variationId,
          t1_b2b: r.b2bPrice,
          t2_b2bT2: r.b2bT2Price,
          hasDifferentT2: r.b2bT2Price !== r.b2bPrice
        }))
      });
      
      // Sample records with different boolean combinations
      const activeMainSample = transformed.find(r => r.isActive && r.isMain);
      const activeNotMainSample = transformed.find(r => r.isActive && !r.isMain);
      const inactiveMainSample = transformed.find(r => !r.isActive && r.isMain);
      
      logger.debug('Boolean combination samples:', {
        activeMain: activeMainSample ? {itemId: activeMainSample.itemId, variationId: activeMainSample.variationId} : 'none',
        activeNotMain: activeNotMainSample ? {itemId: activeNotMainSample.itemId, variationId: activeNotMainSample.variationId} : 'none',
        inactiveMain: inactiveMainSample ? {itemId: inactiveMainSample.itemId, variationId: inactiveMainSample.variationId} : 'none'
      });
    }
    
    return transformed;
  }

  hasCsvData(): boolean {
    const data = this.getCsvData();
    return data.length > 0;
  }

  // Get all available categories from CSV data
  getAvailableCategories(includeSystemCategories: boolean = false): Category[] {
    const csvData = this.getCsvData();
    if (!csvData || csvData.length === 0) return [];
    
    const categoryMap = new Map<string, { name: string; count: number }>();
    
    // First, group by item ID to identify products
    const itemGroups = new Map<string, CsvProductData[]>();
    csvData.forEach(row => {
      if (!itemGroups.has(row.itemId)) {
        itemGroups.set(row.itemId, []);
      }
      itemGroups.get(row.itemId)!.push(row);
    });
    
    // Collect categories only from products that have category 29
    itemGroups.forEach((variations, itemId) => {
      // Check if this product has category 29
      const hasCategory29 = variations.some(variation => {
        const varCategoryIds = String(variation.allCategoryIds || '');
        const varCategoryIdsList = varCategoryIds.split(',').map(id => id.trim()).filter(id => id);
        return varCategoryIdsList.includes('29');
      });
      
      if (!hasCategory29) {
        return; // Skip products without category 29
      }
      
      // Collect all categories from this product's variations (just names, not counts)
      variations.forEach(row => {
        const categoryIds = (row.allCategoryIds || '').split(',').map(id => id.trim()).filter(id => id);
        const categoryNames = (row.allCategories || '').split(',').map(name => name.trim()).filter(name => name);
        
        categoryIds.forEach((id, index) => {
          if (id && !categoryMap.has(id)) {
            const name = categoryNames[index] || `Category ${id}`;
            categoryMap.set(id, { name, count: 0 }); // count is not used anymore
          }
        });
      });
    });
    
    // Convert to Category array and sort by product count
    let categories = Array.from(categoryMap.entries())
      .map(([id, data]) => ({
        id,
        name: data.name,
        nameDE: data.name, // Original German name from CSV
      }));
    
    // Filter out system categories unless explicitly requested
    if (!includeSystemCategories) {
      categories = categories.filter(cat => cat.id !== '21' && cat.id !== '29' && cat.id !== '48' && cat.id !== '256');
    }
    
    // Sort alphabetically by name
    categories.sort((a, b) => a.name.localeCompare(b.name));
    
    logger.debug(`Found ${categories.length} categories in CSV data`);
    return categories;
  }

  // Get product counts per category efficiently
  getCategoryProductCounts(): Record<string, number> {
    const csvData = this.getCsvData();
    if (!csvData || csvData.length === 0) {
      return {};
    }
    
    // Create a map to store unique products per category
    const categoryProductMap = new Map<string, Set<string>>();
    
    // Group products by item ID first
    const itemGroups = new Map<string, CsvProductData[]>();
    csvData.forEach(row => {
      if (!itemGroups.has(row.itemId)) {
        itemGroups.set(row.itemId, []);
      }
      itemGroups.get(row.itemId)!.push(row);
    });
    
    // For each product (item), check which categories it belongs to
    itemGroups.forEach((variations, itemId) => {
      // First check if this product has category 29
      const hasCategory29 = variations.some(variation => {
        const varCategoryIds = String(variation.allCategoryIds || '');
        const varCategoryIdsList = varCategoryIds.split(',').map(id => id.trim()).filter(id => id);
        return varCategoryIdsList.includes('29');
      });
      
      if (!hasCategory29) {
        return; // Skip products without category 29
      }
      const allCategoryIds = new Set<string>();
      
      // Collect all category IDs from all variations of this product
      variations.forEach(variation => {
        const categoryIds = (variation.allCategoryIds || '').split(',').map(id => id.trim()).filter(id => id);
        categoryIds.forEach(id => {
          if (id) {
            allCategoryIds.add(id);
          }
        });
      });
      
      // Add this product to each category it belongs to
      allCategoryIds.forEach(categoryId => {
        if (!categoryProductMap.has(categoryId)) {
          categoryProductMap.set(categoryId, new Set());
        }
        categoryProductMap.get(categoryId)!.add(itemId);
      });
    });
    
    // Convert to counts
    const counts: Record<string, number> = {};
    categoryProductMap.forEach((productSet, categoryId) => {
      counts[categoryId] = productSet.size;
    });
    
    return counts;
  }

  // Get total product count (not variation count)
  getTotalProductCount(): number {
    const csvData = this.getCsvData();
    if (!csvData || csvData.length === 0) return 0;
    
    // Group by item ID to count unique products
    const itemGroups = new Map<string, CsvProductData[]>();
    csvData.forEach(row => {
      if (!itemGroups.has(row.itemId)) {
        itemGroups.set(row.itemId, []);
      }
      itemGroups.get(row.itemId)!.push(row);
    });
    
    // Count only products with category 29
    let productCount = 0;
    itemGroups.forEach((variations, itemId) => {
      const hasCategory29 = variations.some(variation => {
        const varCategoryIds = String(variation.allCategoryIds || '');
        const varCategoryIdsList = varCategoryIds.split(',').map(id => id.trim()).filter(id => id);
        return varCategoryIdsList.includes('29');
      });
      
      if (hasCategory29) {
        productCount++;
      }
    });
    
    return productCount;
  }

  getCsvDataTimestamp(): Date | null {
    // Return URL data timestamp if available, otherwise fixed timestamp for embedded data
    if (this.urlCsvTimestamp) {
      return this.urlCsvTimestamp;
    }
    return new Date('2024-01-01T00:00:00Z');
  }

  // Fetch CSV data from URL
  async refreshCsvFromUrl(forceRefresh: boolean = false): Promise<{ success: boolean; error?: string; recordCount?: number }> {
    if (!this.csvUrl) {
      const error = 'No CSV URL set, cannot refresh from URL';
      logger.debug(error);
      return { success: false, error };
    }

    try {
      logger.debug(`Fetching CSV from URL: ${this.csvUrl} (forceRefresh: ${forceRefresh})`);
      
      const response = await plentyOneApi.fetchCsvFromUrl(this.csvUrl, forceRefresh);
      
      logger.debug(`CSV fetch successful:`, {
        recordCount: response.recordCount,
        source: response.source,
        timestamp: response.timestamp,
        hasError: !!response.error
      });

      // Validate response data
      if (!response.data || !Array.isArray(response.data)) {
        const error = 'Invalid CSV data format received from server';
        logger.error(error, response);
        return { success: false, error };
      }

      // Update internal state with fetched data
      this.urlCsvData = response.data as CsvProductData[];
      this.urlCsvTimestamp = new Date(response.timestamp);

      if (response.error) {
        logger.warn(`CSV fetch completed with warning: ${response.error}`);
        return { success: true, error: response.error, recordCount: response.recordCount };
      }

      return { success: true, recordCount: response.recordCount };
    } catch (error) {
      logger.error('Failed to fetch CSV from URL:', error);
      logger.debug('Keeping existing data or falling back to embedded data');
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      return { success: false, error: errorMessage };
    }
  }

  async getProducts(params: {
    page?: number;
    itemsPerPage?: number;
    search?: string;
    categoryIds?: string[];
  } = {}): Promise<{
    products: Product[];
    total: number;
    currentPage: number;
    totalPages: number;
  }> {
    const { page = 1, itemsPerPage = 12, search = "", categoryIds = [] } = params;
    
    logger.debug("🔍 DEBUG - CsvProductService.getProducts called with params:", params);
    logger.debug("🔍 DEBUG - categoryIds parameter:", {
      categoryIds,
      type: typeof categoryIds,
      isArray: Array.isArray(categoryIds),
      length: categoryIds?.length || 0,
      values: categoryIds
    });

    const csvData = this.getCsvData();
    
    if (csvData.length === 0) {
      logger.debug("No embedded CSV data available");
      return {
        products: [],
        total: 0,
        currentPage: page,
        totalPages: 0
      };
    }

    // Convert CSV data to products (skip stock fetch for performance)
    logger.debug("🔍 DEBUG - About to call csvDataToProducts with categoryIds:", categoryIds);
    let allProducts = await this.csvDataToProducts(csvData, true, categoryIds);
    logger.debug("🔍 DEBUG - csvDataToProducts returned", allProducts.length, "products");

    // Apply search filter with relevance scoring
    if (search && search.trim()) {
      const searchTerm = search.toLowerCase().trim();
      
      // Filter products by search criteria:
      // - SKU: exact match OR prefix match
      // - Product name: partial match allowed
      // - Variation SKU: exact match OR prefix match
      const scoredProducts = allProducts
        .filter(product => {
          const nameMatch = product.name.toLowerCase().includes(searchTerm);
          const skuExactMatch = product.sku.toLowerCase() === searchTerm;
          const skuPrefixMatch = product.sku.toLowerCase().startsWith(searchTerm);
          const variationExactMatch = product.variations.some(v => v.sku.toLowerCase() === searchTerm);
          const variationPrefixMatch = product.variations.some(v => v.sku.toLowerCase().startsWith(searchTerm));
          return nameMatch || skuExactMatch || skuPrefixMatch || variationExactMatch || variationPrefixMatch;
        })
        .map(product => {
          let score = 0;
          const productId = product.id.toLowerCase();
          const productSku = product.sku.toLowerCase();
          const productName = product.name.toLowerCase();
          
          // Highest priority: Exact variation SKU match
          const exactVariationMatch = product.variations.some(v => v.sku.toLowerCase() === searchTerm);
          if (exactVariationMatch) {
            score += 1500; // Highest score for exact variation SKU match
          }
          
          // Very high priority: Exact product SKU match
          if (productSku === searchTerm) {
            score += 1200;
          }
          
          // High priority: Exact ID match
          if (productId === searchTerm) {
            score += 1000;
          }
          
          // Medium-high priority: SKU starts with search term
          if (productSku.startsWith(searchTerm) && productSku !== searchTerm) {
            score += 800;
          }
          
          // Medium priority: ID starts with search term
          if (productId.startsWith(searchTerm) && productId !== searchTerm) {
            score += 700;
          }
          
          // Medium priority: Name starts with search term
          if (productName.startsWith(searchTerm)) {
            score += 600;
          }
          
          // SKU partial matches are not allowed - removed this scoring
          
          // Lower priority: Contains in name (not at start)
          if (productName.includes(searchTerm) && !productName.startsWith(searchTerm)) {
            score += 300;
          }
          
          // Variation SKU prefix matches (but not exact matches)
          const variationPrefixMatches = product.variations.filter(v => 
            v.sku.toLowerCase().startsWith(searchTerm) && v.sku.toLowerCase() !== searchTerm
          );
          if (variationPrefixMatches.length > 0) {
            score += 100 + (variationPrefixMatches.length * 10);
          }
          
          // Bonus for shorter SKUs (more specific matches) - only for prefix matches
          if (productSku.startsWith(searchTerm)) {
            score += Math.max(0, 50 - productSku.length);
          }
          
          return { product, score };
        })
        .sort((a, b) => {
          // Sort by relevance score (descending), then by SKU (ascending)
          if (b.score !== a.score) {
            return b.score - a.score;
          }
          return a.product.sku.localeCompare(b.product.sku);
        })
        .map(item => item.product);
      
      allProducts = scoredProducts;
    }

    // Apply pagination
    const total = allProducts.length;
    const totalPages = Math.ceil(total / itemsPerPage);
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const products = allProducts.slice(startIndex, endIndex);

    logger.debug("CsvProductService: Returning products (fast mode)", {
      productsCount: products.length,
      total,
      currentPage: page,
      totalPages
    });

    return {
      products,
      total,
      currentPage: page,
      totalPages
    };
  }


  private async csvDataToProducts(csvData: CsvProductData[], skipStockFetch: boolean = false, categoryIds: string[] = []): Promise<Product[]> {
    logger.debug(`🔍 DEBUG - csvDataToProducts called with:`, {
      csvDataLength: csvData.length,
      skipStockFetch,
      categoryIds,
      categoryIdsLength: categoryIds.length,
      categoryIdsValues: categoryIds
    });
    
    // Debug: Log first few records to see structure
    if (csvData.length > 0) {
      logger.debug('First CSV record structure:', csvData[0]);
      logger.debug('Available columns:', Object.keys(csvData[0]));
      // Check if CSV records contain category data
      logger.debug('🔍 DEBUG - Sample CSV category data:', {
        record1: {
          itemId: csvData[0].itemId,
          allCategoryIds: csvData[0].allCategoryIds,
          allCategories: csvData[0].allCategories
        },
        record2: csvData[1] ? {
          itemId: csvData[1].itemId,
          allCategoryIds: csvData[1].allCategoryIds,
          allCategories: csvData[1].allCategories
        } : 'N/A'
      });
    }
    
    // Group by item ID to create products with variations
    const productMap = new Map<string, {
      itemId: string;
      productNameDE: string;
      productNameEN: string;
      mainVariationNumber: string; // メインバリエーションのnumber
      variations: CsvProductData[];
    }>();

    // Separate main variations and active variations
    const mainVariations = csvData.filter(item => item.isMain);
    const activeVariations = csvData.filter(item => item.isActive);
    
    logger.debug(`Found ${mainVariations.length} main variations and ${activeVariations.length} active variations out of ${csvData.length} total`);
    
    // Debug: Check what boolean values we have
    const isMainValues = [...new Set(csvData.map(item => item.isMain))];
    const isActiveValues = [...new Set(csvData.map(item => item.isActive))];
    logger.debug('Unique isMain values found:', isMainValues);
    logger.debug('Unique isActive values found:', isActiveValues);
    
    // Debug: Show samples of each type
    if (csvData.length > 0) {
      logger.debug('Sample records with isMain/isActive:', 
        csvData.slice(0, 3).map(item => ({ 
          itemId: item.itemId, 
          variationId: item.variationId, 
          variationNumber: item.variationNumber,
          isMain: item.isMain, 
          isActive: item.isActive,
          attributeValues: item.attributeValues
        }))
      );
    }

    // Group variations by itemId to understand the structure better
    const itemGroups = new Map<string, { main: CsvProductData[], active: CsvProductData[] }>();
    csvData.forEach(item => {
      if (!itemGroups.has(item.itemId)) {
        itemGroups.set(item.itemId, { main: [], active: [] });
      }
      const group = itemGroups.get(item.itemId)!;
      if (item.isMain) group.main.push(item);
      if (item.isActive) group.active.push(item);
    });

    // Debug: Show first few item groups
    const firstItems = Array.from(itemGroups.entries()).slice(0, 5);
    logger.debug('First 5 item groups structure:', firstItems.map(([itemId, group]) => ({
      itemId,
      mainCount: group.main.length,
      activeCount: group.active.length,
      mainVariations: group.main.map(m => ({ 
        variationNumber: m.variationNumber, 
        attributes: m.attributeValues 
      })),
      activeVariations: group.active.map(a => ({ 
        variationNumber: a.variationNumber, 
        attributes: a.attributeValues 
      }))
    })));

    // Check if we have any items with multiple variations
    const itemsWithMultipleVariations = Array.from(itemGroups.entries()).filter(([_, group]) => 
      (group.main.length + group.active.length) > 1
    );
    logger.debug(`Items with multiple variations: ${itemsWithMultipleVariations.length} out of ${itemGroups.size}`);
    
    if (itemsWithMultipleVariations.length > 0) {
      logger.debug('Sample items with multiple variations:', 
        itemsWithMultipleVariations.slice(0, 2).map(([itemId, group]) => ({
          itemId,
          totalVariations: group.main.length + group.active.length,
          variations: [...group.main, ...group.active].map(v => ({
            variationNumber: v.variationNumber,
            attributes: v.attributeValues,
            isMain: v.isMain,
            isActive: v.isActive
          }))
        }))
      );
    }

    // Create product map with main variation info
    mainVariations.forEach(mainVar => {
      productMap.set(mainVar.itemId, {
        itemId: mainVar.itemId,
        productNameDE: mainVar.productNameDE,
        productNameEN: mainVar.productNameEN,
        mainVariationNumber: mainVar.variationNumber,
        variations: []
      });
    });

    // Add active variations to their respective products
    activeVariations.forEach(item => {
      if (productMap.has(item.itemId)) {
        productMap.get(item.itemId)!.variations.push(item);
      } else {
        // If no main variation found, create product entry
        logger.warn(`No main variation found for item ${item.itemId}, creating fallback entry`);
        productMap.set(item.itemId, {
          itemId: item.itemId,
          productNameDE: item.productNameDE,
          productNameEN: item.productNameEN,
          mainVariationNumber: item.itemId, // fallback to item ID
          variations: [item]
        });
      }
    });

    // Enhanced logic: collect ALL variations for each item (both main and active)
    logger.debug(`Before variation assignment: productMap has ${productMap.size} items`);
    
    // First, filter items by category ID 29 before assigning variations
    const itemsToRemove = new Set<string>();
    
    productMap.forEach((productGroup, itemId) => {
      const itemGroup = itemGroups.get(itemId);
      if (!itemGroup) {
        logger.warn(`No itemGroup found for itemId ${itemId} in productMap`);
        itemsToRemove.add(itemId);
        return;
      }
      
      // Check if any variation has the required category IDs
      const allVariationsForItem = csvData.filter(v => v.itemId === itemId);
      
      // IMPORTANT: Check if product has category 29 (B2B product category)
      const hasCategory29 = allVariationsForItem.some(variation => {
        const varCategoryIds = String(variation.allCategoryIds || '');
        const varCategoryIdsList = varCategoryIds.split(',').map(id => id.trim()).filter(id => id);
        return varCategoryIdsList.includes('29');
      });
      
      if (!hasCategory29) {
        logger.debug(`🔍 DEBUG - Item ${itemId}: Does NOT have category 29 - excluding from B2B system`);
        itemsToRemove.add(itemId);
        return;
      }
      
      // If category filter is specified, check if product matches
      if (categoryIds.length > 0) {
        logger.debug(`🔍 DEBUG - Applying category filter for item ${itemId}`);
        
        const hasRequiredCategory = allVariationsForItem.some(variation => {
          const varCategoryIds = String(variation.allCategoryIds || '');
          const varCategoryIdsList = varCategoryIds.split(',').map(id => id.trim()).filter(id => id);
          
          logger.debug(`🔍 DEBUG - Item ${itemId} variation ${variation.variationId} categories:`, {
            rawCategoryIds: variation.allCategoryIds,
            parsedCategoryIds: varCategoryIdsList,
            lookingFor: categoryIds
          });
          
          // Check if product has at least one of the selected categories
          const hasMatch = categoryIds.some(selectedId => varCategoryIdsList.includes(selectedId));
          if (hasMatch) {
            logger.debug(`🔍 DEBUG - MATCH FOUND for item ${itemId} with category filter`);
          }
          return hasMatch;
        });
        
        if (!hasRequiredCategory) {
          logger.debug(`🔍 DEBUG - Item ${itemId}: NO MATCH - excluding from results`);
          itemsToRemove.add(itemId);
          return;
        } else {
          logger.debug(`🔍 DEBUG - Item ${itemId}: HAS MATCH - including in results`);
        }
      }
      
      // If we have active variations, use them
      if (itemGroup.active.length > 0) {
        logger.debug(`Item ${itemId}: Using ${itemGroup.active.length} active variations`);
        productGroup.variations = itemGroup.active;
      } 
      // If no active variations but have main variations, use main variations as sellable
      else if (itemGroup.main.length > 0) {
        logger.debug(`Item ${itemId}: Using ${itemGroup.main.length} main variations as sellable`);
        productGroup.variations = itemGroup.main;
      }
      // Last resort: collect all variations regardless of status
      else {
        logger.debug(`Item ${itemId}: Using all ${allVariationsForItem.length} variations as fallback`);
        productGroup.variations = allVariationsForItem;
      }
      
      logger.debug(`Item ${itemId} final variation count: ${productGroup.variations.length}`);
    });
    
    // Remove items that don't match the category filter
    itemsToRemove.forEach(itemId => {
      productMap.delete(itemId);
    });
    
    logger.debug(`After variation assignment: productMap ready for Product conversion`);

    const products: Product[] = [];

    logger.debug(`Processing ${productMap.size} product groups for conversion to Product objects`);

    productMap.forEach((productGroup) => {
      if (productGroup.variations.length === 0) {
        logger.warn(`Product ${productGroup.itemId} has no variations after processing, skipping`);
        return;
      }

      logger.debug(`Processing product ${productGroup.itemId} with ${productGroup.variations.length} variations`);

      const variations: ProductVariation[] = productGroup.variations.map(variation => {
        const stock = skipStockFetch ? 0 : 0; // Will be fetched separately when needed
        let extractedColor = this.extractColorFromAttributes(variation.attributeValues);
        let extractedSize = this.extractSizeFromAttributes(variation.attributeValues);
        
        // Fallback: if attributes are empty, try to extract from SKU
        if (!extractedColor && !extractedSize) {
          const skuInfo = this.extractVariationInfoFromSKU(variation.variationNumber);
          extractedColor = skuInfo.color || '';
          extractedSize = skuInfo.size || '';
          
          if (extractedColor || extractedSize) {
            logger.debug(`Variation ${variation.variationNumber}: fallback from SKU -> color="${extractedColor}", size="${extractedSize}"`);
          }
        }
        
        logger.debug(`Variation ${variation.variationNumber}: attributes="${variation.attributeValues}" -> color="${extractedColor}", size="${extractedSize}"`);
        
        // Calculate UVP NET price
        const uvpNet = roundPrice((variation.uvpPrice || 0) / 1.19);
        // Use B2B price from CSV (already in DB), convert from gross to net
        const b2bNet = roundPrice((variation.b2bPrice || 0) / 1.19);
        const b2bNet55 = roundPrice(uvpNet * 0.55); // Still calculate 55% for tier discounts
        
        return {
          id: variation.variationId,
          sku: variation.variationNumber,
          color: extractedColor || '標準',
          size: extractedSize || '標準サイズ',
          // All prices are now NET and properly rounded
          price: uvpNet,
          priceB2B: b2bNet,  // Use DB price, not calculated
          priceB2B55: b2bNet55,
          // Keep T1/T2 prices for reference (these are from CSV, might be gross)
          priceT1: variation.b2bT1Price || undefined,
          priceT2: variation.b2bT2Price || undefined,
          stock: stock,
          available: skipStockFetch ? true : stock > 0  // Always available if skipping stock fetch
        };
      });

      const colors = [...new Set(variations.map(v => v.color))];
      const sizes = [...new Set(variations.map(v => v.size))];
      const totalStock = skipStockFetch ? 0 : variations.reduce((sum, v) => sum + v.stock, 0);
      
      // Use UVP price as basePrice for product listing
      // Get the first variation's prices to use as base
      const firstVariation = productGroup.variations[0];
      const basePrice = firstVariation.uvpPrice || firstVariation.b2bT1Price || 0;

      // Prefer German name, fallback to English
      const productName = productGroup.productNameDE || productGroup.productNameEN;

      logger.debug(`Product ${productGroup.itemId} names: DE="${productGroup.productNameDE}", EN="${productGroup.productNameEN}", using="${productName}"`);
      logger.debug(`Product ${productGroup.itemId} "${productName}": ${variations.length} variations, ${colors.length} colors [${colors.join(', ')}], ${sizes.length} sizes [${sizes.join(', ')}]`);

      // Extract category IDs from variations
      const categoryIdsSet = new Set<number>();
      productGroup.variations.forEach(variation => {
        const categoryIds = variation.allCategoryIds || '';
        if (categoryIds) {
          categoryIds.split(',').forEach(id => {
            const trimmedId = id.trim();
            if (trimmedId) {
              categoryIdsSet.add(parseInt(trimmedId, 10));
            }
          });
        }
      });
      const categoryIds = Array.from(categoryIdsSet);

      products.push({
        id: productGroup.itemId,
        name: productName,
        sku: productGroup.mainVariationNumber, // Use main variation number as product ID
        description: `${productName}の詳細説明`,
        basePrice: basePrice || 0,
        variations,
        colors,
        sizes,
        category: 'その他',
        categoryId: categoryIds.includes(29) ? 29 : (categoryIds[0] || undefined),
        categoryIds, // Store all category IDs for reference
        images: [],
        totalStock,
        isNew: categoryIds ? categoryIds.includes(256) : false // Set isNew flag if product has category 256
      });
    });

    // No SKU filtering - include all products that match the category filter
    const filteredProducts = products;
    
    // Log warning about SKU filtering removal
    logger.debug(`WARNING: SKU filtering has been removed. All products matching the category filter will be included.`);
    logger.debug(`Category filter: ${categoryIds.length > 0 ? categoryIds.join(', ') : 'None (showing all products)'}`);

    // Sort products by SKU (only when no search is applied)
    // When search is applied, products are already sorted by relevance
    const sortedProducts = filteredProducts.sort((a, b) => {
      const skuA = a.sku || '';
      const skuB = b.sku || '';
      return skuA.localeCompare(skuB);
    });

    logger.debug(`🔍 DEBUG - === CSV Data Processing Summary ===`);
    logger.debug(`Input CSV records: ${csvData.length}`);
    logger.debug(`Unique items found: ${itemGroups.size}`);
    
    // Count items with category 29
    const itemsWithCategory29 = Array.from(itemGroups.entries()).filter(([itemId, _]) => {
      const allVariationsForItem = csvData.filter(v => v.itemId === itemId);
      return allVariationsForItem.some(variation => {
        const varCategoryIds = String(variation.allCategoryIds || '');
        const varCategoryIdsList = varCategoryIds.split(',').map(id => id.trim()).filter(id => id);
        return varCategoryIdsList.includes('29');
      });
    }).length;
    
    logger.debug(`Items with category 29 (B2B products): ${itemsWithCategory29}`);
    logger.debug(`Items excluded (no category 29): ${itemGroups.size - itemsWithCategory29}`);
    
    if (categoryIds.length > 0) {
      logger.debug(`Additional category filter applied: ${categoryIds.join(',')}`);
      logger.debug(`Items after additional filter: ${productMap.size}`);
    }
    
    logger.debug(`Main variations found: ${mainVariations.length}`);
    logger.debug(`Active variations found: ${activeVariations.length}`);
    logger.debug(`Products created: ${products.length}`);
    logger.debug(`Final sorted products: ${sortedProducts.length}`);
    
    // Show sample of products that would have been excluded by old SKU filtering
    const wouldHaveBeenExcluded = products.filter(product => {
      const sku = product.sku || '';
      
      // 2-、3-、99で始まるもの
      if (sku.startsWith('2-') || sku.startsWith('3-') || sku.startsWith('99')) {
        return true;
      }
      
      // 1-9の数字以外で始まるもの（文字で始まるSKUなど）
      const firstChar = sku.charAt(0);
      if (firstChar && (firstChar < '1' || firstChar > '9')) {
        return true;
      }
      
      return false;
    });
    if (wouldHaveBeenExcluded.length > 0) {
      logger.debug(`NOTE: ${wouldHaveBeenExcluded.length} products are now included that would have been excluded:`, wouldHaveBeenExcluded.slice(0, 10).map(p => p.sku));
    }
    
    return sortedProducts;
  }

  private extractColorFromAttributes(attributeValues: string): string {
    if (!attributeValues) return '';
    
    // Parse attribute values like "Gr.:35-38,Fb.:schwarz (09)"
    // Extract color from "Fb.:colorname" pattern
    const colorMatch = attributeValues.match(/Fb\.:([^,()]+)(?:\s*\([^)]*\))?/);
    if (colorMatch) {
      return colorMatch[1].trim();
    }
    
    return '';
  }

  private extractSizeFromAttributes(attributeValues: string): string {
    if (!attributeValues) return '';
    
    // Parse attribute values like "Gr.:35-38,Fb.:schwarz (09)"  
    // Extract size from "Gr.:sizeinfo" pattern
    const sizeMatch = attributeValues.match(/Gr\.:([^,]+)/);
    if (sizeMatch) {
      return sizeMatch[1].trim();
    }
    
    return '';
  }

  // Fallback: try to extract variation info from variationNumber when attributes are empty
  private extractVariationInfoFromSKU(variationNumber: string): { color?: string; size?: string } {
    // Common patterns in variation numbers that might indicate variants
    // This is a fallback when attributeValues is empty
    
    // Look for color indicators in SKU
    let color = '';
    let size = '';
    
    // Common color abbreviations in German product SKUs
    if (variationNumber.includes('schwarz') || variationNumber.includes('blk')) color = 'schwarz';
    else if (variationNumber.includes('weiss') || variationNumber.includes('wht')) color = 'weiß';
    else if (variationNumber.includes('grau') || variationNumber.includes('gry')) color = 'grau';
    else if (variationNumber.includes('navy') || variationNumber.includes('nvy')) color = 'navy';
    else if (variationNumber.includes('rot') || variationNumber.includes('red')) color = 'rot';
    
    // Look for size indicators
    const sizeMatch = variationNumber.match(/(\d{2}-\d{2}|S|M|L|XL|XXL)/i);
    if (sizeMatch) {
      size = sizeMatch[1];
    }
    
    return { color, size };
  }

  // Get products by SKU - supports both product SKU and variation SKU
  async getProductsBySku(sku: string): Promise<Product[]> {
    const csvData = this.getCsvData();
    logger.debug(`getProductsBySku called for SKU: ${sku}, CSV data rows: ${csvData?.length || 0}`);
    
    if (!csvData || csvData.length === 0) {
      logger.debug('No CSV data available for SKU search');
      return [];
    }

    const results: Product[] = [];
    const processedItemIds = new Set<string>();

    // Search through all variations
    for (const row of csvData) {
      // Skip if we've already processed this item
      if (processedItemIds.has(row.itemId)) {
        continue;
      }

      // Check if this variation's SKU matches
      if (row.variationNumber === sku) {
        logger.debug(`Found matching SKU ${sku} in item ${row.itemId}`);
        
        // Found a match - get all variations for this product
        const productVariations = csvData.filter(v => v.itemId === row.itemId);
        logger.debug(`Item ${row.itemId} has ${productVariations.length} variations`);
        
        // Check if product has category 29
        const hasCategory29 = productVariations.some(variation => {
          const categoryIds = (variation.allCategoryIds || '').split(',').map(id => id.trim());
          return categoryIds.includes('29');
        });
        
        if (!hasCategory29) {
          logger.debug(`Item ${row.itemId} does not have category 29, skipping`);
          processedItemIds.add(row.itemId);
          continue;
        }
        
        // Create a product from these variations
        const product = this.createProductFromVariations(row.itemId, productVariations);
        if (product) {
          results.push(product);
          processedItemIds.add(row.itemId);
        }
      }
    }

    logger.debug(`Found ${results.length} products for SKU: ${sku}`);
    return results;
  }

  // Helper method to create a Product from CSV variations
  private createProductFromVariations(itemId: string, variations: CsvProductData[]): Product | null {
    if (variations.length === 0) return null;

    // Get the first variation as reference
    const firstVariation = variations[0];
    
    // Determine product name
    const productName = firstVariation.productNameEN || firstVariation.productNameDE || `Product ${itemId}`;
    
    // Convert variations to ProductVariation objects
    const productVariations: ProductVariation[] = variations.map(variation => {
      const color = this.extractColorFromAttributes(variation.attributeValues);
      const size = this.extractSizeFromAttributes(variation.attributeValues);
      
      // Calculate all prices from gross UVP
      const prices = this.calculatePrices(variation.uvpPrice || 0);
      
      return {
        id: variation.variationId,
        sku: variation.variationNumber,
        color: color || 'Default',
        size: size || 'One Size',
        // All prices are now NET and properly rounded
        price: prices.uvpNet,
        priceB2B: prices.b2bPrice58,
        priceB2B55: prices.b2bPrice55,
        // Keep T1/T2 prices for reference (these are from CSV, might be gross)
        priceT1: variation.b2bT1Price || undefined,
        priceT2: variation.b2bT2Price || undefined,
        stock: 0, // Stock will be fetched separately
        available: variation.isActive
      };
    });

    // Extract unique colors and sizes
    const colors = [...new Set(productVariations.map(v => v.color))];
    const sizes = [...new Set(productVariations.map(v => v.size))];

    // Get category information
    const categoryIds = (firstVariation.allCategoryIds || '').split(',').map(id => id.trim()).filter(id => id).map(Number);
    const categoryNames = (firstVariation.allCategories || '').split(',').map(name => name.trim());
    const mainCategory = categoryNames[0] || 'Other';

    return {
      id: itemId,
      name: productName,
      sku: firstVariation.variationNumber,
      description: productName,
      basePrice: firstVariation.uvpPrice || firstVariation.b2bPrice,
      variations: productVariations,
      colors,
      sizes,
      category: mainCategory,
      categoryId: categoryIds[0],
      categoryIds,
      images: [],
      totalStock: 0
    };
  }

}

export const csvProductService = new CsvProductService();
